import React, { useState } from 'react';
import { StatusPill, ConfirmDialog } from '../../ui';
import { associationService } from '../../../services/associationService';
import { ParcelReview } from './ParcelReview';

const RESEARCH_PILL = {
  pending: { label: 'Research pending', variant: 'pending' },
  running: { label: 'Researching…', variant: 'pending' },
  done: { label: 'Research done', variant: 'approved' },
  unsupported: { label: 'Outside Montana', variant: 'info' },
  failed: { label: 'Research failed', variant: 'denied' },
  skipped: { label: 'No address yet', variant: 'info' },
};

const slugify = (name) => String(name || '')
  .toLowerCase()
  .replace(/\b(hoa|homeowners|owners|association|inc|condominium|condo)\b/g, ' ')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40);

const inputClass = 'w-full rounded-2xl border border-amber-100/60 bg-white px-4 py-2.5 text-sm font-medium text-primary-dark focus:outline-none focus:ring-2 focus:ring-secondary/40';
const primaryButton = 'bg-secondary text-white px-6 py-2.5 rounded-full text-sm font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50';
const ghostButton = 'border border-secondary text-secondary px-6 py-2.5 rounded-full text-sm font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50';

// One onboarding lead: research summary, parcel review, provisioning, import of the
// reviewed homes, and the first admin invite — in that order.
export function LeadCard({ lead, associations, onChanged }) {
  const summary = lead.research_summary || {};
  const association = associations.find((a) => a.id === lead.hoa_id) || null;
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [showParcels, setShowParcels] = useState(false);
  const [confirmImport, setConfirmImport] = useState(false);
  const [form, setForm] = useState({
    name: lead.organization || '',
    slug: slugify(lead.organization),
    plan: (lead.plan || 'starter').toLowerCase(),
    trialDays: 30,
  });
  const [invite, setInvite] = useState({ email: lead.contact_email || '', fullName: lead.contact_name || '' });
  const [extraAddress, setExtraAddress] = useState('');
  const additionalAddresses = summary.additional_addresses || [];

  const run = async (label, fn, success) => {
    setBusy(label);
    setMessage(null);
    try {
      const result = await fn();
      setMessage({ tone: 'ok', text: typeof success === 'function' ? success(result) : success });
      onChanged?.();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const pill = RESEARCH_PILL[lead.research_status] || RESEARCH_PILL.pending;

  return (
    <article className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30 space-y-4" data-testid="lead-card">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-primary-dark">{lead.organization || 'Unnamed lead'}</h3>
          <p className="text-sm font-medium text-slate-500">{lead.community_address || 'No community address yet'}</p>
          {additionalAddresses.map((a) => (
            <p key={a} className="text-sm font-medium text-slate-500">+ {a}</p>
          ))}
          <p className="text-[10px] font-bold uppercase tracking-tighter text-slate-500 mt-1">
            {lead.source === 'website' ? 'Website form' : 'Added by staff'} · {new Date(lead.created_at).toLocaleDateString()}
            {lead.plan ? ` · ${lead.plan} plan` : ''}
            {lead.contact_email ? ` · ${lead.contact_email}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusPill label={pill.label} variant={pill.variant} />
          {association && <StatusPill label={`Portal: ${association.slug}`} variant="published" />}
        </div>
      </header>

      {lead.research_status === 'done' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: summary.association_kind === 'condominium' ? 'Condo units' : 'Homes', value: summary.units },
            { label: 'Distinct owners', value: summary.distinct_owners },
            { label: 'Likely owner-occupied', value: summary.likely_owner_occupied },
            { label: 'Out-of-state owners', value: summary.out_of_state_owners },
          ].map((s) => (
            <div key={s.label} className="bg-section-bg rounded-2xl p-3">
              <div className="text-2xl font-extrabold tracking-tight text-secondary">{s.value ?? '—'}</div>
              <div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {lead.research_status === 'done' && summary.base_name && (
        <p className="text-sm font-medium text-slate-600">
          Matched <strong>{summary.matched_address}</strong> ({summary.county} County) to{' '}
          <strong>{summary.base_name}</strong>{summary.association_kind ? ` — a ${summary.association_kind}` : ''}.
        </p>
      )}
      {lead.research_error && <p className="text-sm font-medium text-amber-700">{lead.research_error}</p>}

      <div className="flex flex-wrap gap-2">
        {lead.community_address && (
          <button type="button" className={ghostButton} disabled={!!busy}
            onClick={() => run('research', () => associationService.runResearch(lead.id),
              (r) => `Research ${r.status}: ${r.summary?.units ?? 0} parcels staged.`)}>
            {busy === 'research' ? 'Researching…' : lead.research_status === 'done' ? 'Re-run research' : 'Run research'}
          </button>
        )}
        {lead.research_status === 'done' && (
          <button type="button" className={ghostButton} onClick={() => setShowParcels(!showParcels)}>
            {showParcels ? 'Hide homes' : 'Review homes'}
          </button>
        )}
      </div>

      {lead.research_status === 'done' && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const address = extraAddress.trim();
            if (!address) return;
            run('research', () => associationService.runResearch(lead.id, address),
              (r) => `Added ${address}: ${r.summary?.units ?? 0} homes across ${1 + (r.summary?.additional_addresses?.length || 0)} addresses.`)
              .then(() => setExtraAddress(''));
          }}
        >
          <label className="text-sm font-medium text-slate-600 flex-1 min-w-[16rem]">
            Another address in the same association
            <span className="block text-xs text-slate-500">For merged associations or phases recorded under another name.</span>
            <input className={inputClass} value={extraAddress} onChange={(e) => setExtraAddress(e.target.value)}
              placeholder="e.g. 1 Main St, Kalispell, MT 59901" />
          </label>
          <button type="submit" className={ghostButton} disabled={!!busy || !extraAddress.trim()}>Add address</button>
        </form>
      )}

      {showParcels && <ParcelReview leadId={lead.id} />}

      {!association && (
        <section className="bg-section-bg rounded-2xl p-5 space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Open a portal</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm font-medium text-slate-600">Association name
              <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugify(e.target.value) })} />
            </label>
            <label className="text-sm font-medium text-slate-600">Web address
              <div className="flex items-center gap-1">
                <input className={inputClass} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} />
                <span className="text-sm font-medium text-slate-500 whitespace-nowrap">.atrionos.com</span>
              </div>
            </label>
            <label className="text-sm font-medium text-slate-600">Plan
              <select className={inputClass} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                {['starter', 'standard', 'pro', 'custom'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-600">Trial length (days)
              <input type="number" min="0" max="180" className={inputClass} value={form.trialDays}
                onChange={(e) => setForm({ ...form, trialDays: Number(e.target.value) })} />
            </label>
          </div>
          <button type="button" className={primaryButton} disabled={!!busy || !form.name || !form.slug}
            onClick={() => run('provision', () => associationService.provision({
              lead_id: lead.id, name: form.name, slug: form.slug, plan: form.plan, trial_days: form.trialDays,
            }), (r) => `Portal created at ${r.slug}.atrionos.com (domain: ${r.steps?.domain?.status}).`)}>
            {busy === 'provision' ? 'Creating…' : 'Create portal'}
          </button>
        </section>
      )}

      {association && (
        <section className="bg-section-bg rounded-2xl p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" className={primaryButton} disabled={!!busy || lead.research_status !== 'done'}
              onClick={() => setConfirmImport(true)}>
              {busy === 'import' ? 'Importing…' : 'Import selected homes'}
            </button>
            <button type="button" className={ghostButton} disabled={!!busy}
              onClick={() => associationService.switchTo(association.slug)}>
              Open portal
            </button>
          </div>
          <p className="text-sm font-medium text-slate-500">
            Homes or people the public record misses: open the portal, then Users, then Import spreadsheet
            (the same template the association's admin can download there), or Add User for one person.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <label className="text-sm font-medium text-slate-600">First admin's name
              <input className={inputClass} value={invite.fullName} onChange={(e) => setInvite({ ...invite, fullName: e.target.value })} />
            </label>
            <label className="text-sm font-medium text-slate-600">First admin's email
              <input type="email" className={inputClass} value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </label>
            <button type="button" className={primaryButton} disabled={!!busy || !invite.email}
              onClick={() => run('invite', () => associationService.inviteFirstAdmin({
                hoaId: association.id, email: invite.email.trim(), fullName: invite.fullName.trim(),
              }), `Invitation sent to ${invite.email.trim()}.`)}>
              {busy === 'invite' ? 'Sending…' : 'Send admin invite'}
            </button>
          </div>
        </section>
      )}

      {message && (
        <p className={`text-sm font-medium ${message.tone === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>{message.text}</p>
      )}

      <ConfirmDialog
        isOpen={confirmImport}
        variant="warning"
        title="Import homes into the portal?"
        message={`The selected public-records parcels become ${association?.name || 'the association'}'s property list. Homes already imported are skipped.`}
        confirmLabel="Import homes"
        onConfirm={() => {
          setConfirmImport(false);
          run('import', () => associationService.importParcels(lead.id), (n) => `${n} homes added to the property list.`);
        }}
        onCancel={() => setConfirmImport(false)}
      />
    </article>
  );
}

export default LeadCard;
